import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import {
  AUTH_PROVIDER_COOKIE,
  readProviderCookie,
} from '@/utils/auth/authProviderCookie';
import {
  type AuthProvider,
  getPersistedAuthProvider,
} from '@/utils/auth/persistedAuthProvider';
import { readAuthRevision } from '@heidi/auth/state';
import { extractExpFromAccessToken } from '@heidi/auth/tokens';
import { getCookie } from 'cookies-next/client';

/**
 * Resolves the effective auth provider for cache-routing decisions.
 *
 * On web, the `heidi-auth-provider` cookie is authoritative — it's set
 * server-side by the OAuth callback before Redux can update, and the
 * Kinde→Auth0 migration flow relies on the cookie flipping ahead of
 * Redux. Falls back to Redux when the cookie is unset or holds an
 * unrecognised value (covers first-boot-with-persisted-state and edge
 * cases where the cookie was cleared but Redux still carries the prior
 * provider).
 *
 * On desktop, the OAuth handshake doesn't set browser cookies, so
 * Redux remains authoritative.
 *
 * Used by every site that branches on the active provider — dispatcher,
 * scheduler, useAuth0Session shim, useIsAuthenticated. Centralising the
 * logic prevents the four sites from drifting apart on the cookie-vs-
 * Redux question.
 *
 * ## Reactivity contract — IMPORTANT for callers
 *
 * The cookie read is sync and non-subscribed. No browser API fires when
 * `document.cookie` is mutated (the Cookie Store API is Chromium-only
 * and not yet portable), so React hooks consuming this helper only
 * re-render when something else changes — typically a Jotai atom written
 * by the cache, or a `useSelector`-subscribed Redux value.
 *
 * The implication: **any code path that writes or clears the cookie
 * MUST pair the write with an atom-driving call** so consumers actually
 * notice. The current callers all satisfy this:
 *
 * - OAuth callback (server-side cookie write) → full page navigation →
 *   scheduler re-mounts → `getAccessToken()` fires → atoms transition.
 * - Kinde→Auth0 migration polling (`useKindeMigrationCompletion`) →
 *   `refreshSession()` → `getAccessToken({ forceRefresh: true })` →
 *   atoms transition.
 * - Forced-logout cookie clear (in `useSessionInvalidationTelemetry`)
 *   → paired with `debouncedExpiryLogout` → drives Redux + atom clears.
 *
 * If a new flow flips the cookie without triggering a fetch (or
 * `clearAuthCache`), consumers will keep returning the stale value
 * until some unrelated atom transition or Redux change forces a
 * re-render. Worst case: provider-breakdown reports the wrong provider
 * until the next focus event runs the scheduler.
 */
export function getEffectiveAuthProvider(): AuthProvider | null {
  const reduxProvider = store.getState().auth.authProvider ?? null;
  if (isTauri()) {
    // Fall back to the persisted-provider read when Redux is null.
    // This covers ONLY the pre-rehydration window — the moment
    // between module import and redux-persist finishing its
    // rehydration into Redux. `getPersistedAuthProvider()` is not
    // a separate store: it reads `authProvider` out of the SAME
    // redux-persist auth slice via `readPersistedAuthSlice`, so a
    // legacy install whose persisted slice never had `authProvider`
    // written gets `undefined` from BOTH reads. Residual legacy
    // state is healed by the always-dispatch paths at the source:
    // Auth0-desktop dispatches `setAuthProviderAsSessionCookie(
    // AUTH0_DESKTOP)` on both boot-restore paths in
    // `src/tauri/auth0/provider.tsx`, and Kinde-desktop dispatches
    // `setAuthProviderAsSessionCookie(KINDE)` inside the
    // post-init effect in `src/components/utils/tauriAuth.tsx`
    // whenever `authProvider !== KINDE`. This fallback is kept
    // because PersistGate doesn't gate every cache-driven caller
    // and a pre-rehydration read of `state.auth.authProvider`
    // would otherwise fall into the dispatcher's null-provider
    // transient gate for the microtask window before rehydration
    // completes.
    return reduxProvider ?? getPersistedAuthProvider() ?? null;
  }
  return readProviderCookie() ?? reduxProvider;
}

/**
 * The runtime-only auth revision, paired here with the effective provider
 * `getEffectiveAuthProvider` resolves so both halves of an auth identity are
 * read through one module.
 *
 * The counter itself no longer lives in the `auth` Redux slice — it is a
 * package-owned register (`@heidi/auth/state`) advanced by the
 * `(token, authProvider)` observer wired in `src/store.ts`. Synchronous, and
 * must stay that way: callers pair it with a token inside one continuation.
 */
export function getCurrentAuthRevision(): number {
  return readAuthRevision();
}

/**
 * The provider exactly as the `auth` slice holds it, for the analytics emit
 * hosts that tag events with it. No cookie or persisted fallback: those belong
 * to `getEffectiveAuthProvider`, and adding one would change the payload.
 */
export function readStoreAuthProviderForAnalytics(): AuthProvider | undefined {
  return store.getState().auth?.authProvider;
}

export type LiveAuthSignal = 'live_token' | 'cookie' | 'none';

/**
 * Which signal (if any) establishes a LIVE auth session — not merely
 * leftover credentials. Decides the Braze gate below ('none' → suppress)
 * and tags the mint-risk telemetry.
 *
 * The original LCM-472 check (`provider cookie || store token`) treated any
 * persisted auth artifact as a session. Both artifacts outlive real
 * sessions: `auth` is redux-persist-whitelisted (a dead token survives
 * until an explicit logout clears it) and the provider cookie's persistent
 * variant lives 30 days. Browsers whose session lapsed WITHOUT a logout
 * (so `resetIdentity` never ran and their stale `ajs_user_id` is intact)
 * therefore passed the check — Braze kept loading on their logged-out
 * pages and the anonymous-profile influx continued.
 *
 * Rule: when a token exists, its JWT `exp` decides — expired or
 * undecodable means NO session, with no cookie fallback (a lapsed
 * web/desktop browser holds BOTH stale artifacts, so any fallback reopens
 * the hole). The cookie fallback covers only tokenless states: most
 * FHIR/EHR/widget launches DO dispatch a store JWT (haiku, sgimed,
 * medtech, widget-auth) and are decided by the exp branch like everyone
 * else — the fallback is for any flow or boot moment where only the
 * provider cookie exists. A stale cookie-without-token combination is
 * rare by construction (explicit logout deletes the cookie; a lapse
 * leaves the token behind, so the exp branch decides false) — accepted
 * residual. Fails toward suppression: a logged-in user missing Braze for
 * one page load self-heals on the next navigation; an anonymous Braze
 * profile is permanent.
 */
export function getLiveAuthSignal(): LiveAuthSignal {
  try {
    const token = store.getState()?.auth?.token;
    if (token) {
      // Epoch milliseconds, or undefined when the token has no decodable
      // numeric `exp` (malformed/opaque tokens count as no session).
      const expiresAtMs = extractExpFromAccessToken(token);
      return expiresAtMs !== undefined && expiresAtMs > Date.now()
        ? 'live_token'
        : 'none';
    }
    return getCookie(AUTH_PROVIDER_COOKIE) ? 'cookie' : 'none';
  } catch {
    // Runs during telemetry init — a throw here would abort the whole
    // Segment load, so never let one escape.
    return 'none';
  }
}
